import { convertErrorToLoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { ErrorMessage } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ErrorMessage";
import { LoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/LoginError";
import { LoginWrapper } from "@faire/retailer-visitor-shared/components/AppSignIn/components/styled";
import { ContactFormLinkFormatting } from "@faire/retailer-visitor-shared/components/Links";
import resetPasswordWithEmail from "@faire/web-api--source/endpoints/www/api/user/reset-password/post";
import resetPassword from "@faire/web-api--source/endpoints/www/api/user/reset-password/userToken/post";
import { ILoginDataRequest } from "@faire/web-api--source/indigofair/data/ILoginDataRequest";
import { ISendResetPasswordRequest } from "@faire/web-api--source/indigofair/data/ISendResetPasswordRequest";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { GrowingSpacer, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC, useState, type JSX } from "react";

interface IProps {
  userToken: string | undefined;
  emailAddress: string | undefined;
}

export const ResetPasswordView: FC<IProps> = ({ userToken, emailAddress }) => {
  const { strictLocalize } = useStrictLocalization();
  const [showResend, setShowResend] = useState(true);
  const [error, setError] = useState<LoginError>();

  const onResend = async () => {
    try {
      if (userToken) {
        await resetPassword(
          userToken,
          ISendResetPasswordRequest.build({ resend: true })
        );
      } else if (emailAddress) {
        await resetPasswordWithEmail(
          ILoginDataRequest.build({
            email_address: emailAddress,
            resend: true,
          })
        );
      } else {
        setError(
          new LoginError(
            strictLocalize({
              id: "M3tiKi",
              defaultMessage:
                "Email address not available. Please contact support.",
              description: {
                text: "Error message if email address is not avaiable.",
              },
            })
          )
        );
      }
      setShowResend(false);
    } catch (e) {
      setError(convertErrorToLoginError(e, strictLocalize));
    }
  };

  return (
    <LoginWrapper>
      <GrowingSpacer />
      <Typography
        variant="pageHeaderSerifRegular"
        data-test-id="check-email-prompt-header"
      >
        {showResend ? (
          <StrictLocalMsg
            id="M0KgNU"
            defaultMessage="Check your email"
            description={{
              text: "An email has been sent to let users reset their password",
            }}
          />
        ) : (
          <StrictLocalMsg
            id="dr4/LJ"
            defaultMessage="Sent!"
            description={{
              text: "Let the user know that an email containing instructions has been sent",
            }}
          />
        )}
      </Typography>
      <Spacer height="1x" />
      <Typography>
        {showResend ? (
          <StrictLocalMsg
            id="xVPevm"
            defaultMessage="We just sent you instructions for resetting your password. Didn't get it? <Link>Resend Email</Link>"
            values={DEPRECATED_backwardsCompatibleValues({
              Link: (chunks: JSX.Element) => (
                <EmbeddedLink onClick={onResend}>{chunks}</EmbeddedLink>
              ),
            })}
          />
        ) : (
          <StrictLocalMsg
            id="4iObSF"
            defaultMessage="Still not seeing the email? <ContactFormLinkFormatting>Let Us Know</ContactFormLinkFormatting>"
            values={{
              ContactFormLinkFormatting,
            }}
          />
        )}
      </Typography>
      <ErrorMessage error={error} />
      <GrowingSpacer />
    </LoginWrapper>
  );
};
